import { useEffect, useMemo, useState } from "react";
import { FileText, ShieldCheck, Ban, XCircle, CheckCircle2, ChevronLeft, ChevronRight, RotateCcw, CreditCard, Coins, MessageCircle, CheckSquare } from "lucide-react";
import client, { extractError } from "@/api";
import { buildWhatsappUrl, buildPaymentRequestMessage, normalizePhoneForWhatsapp } from "@/lib/whatsapp";
import { AdminMembershipAgreementPanel } from "@/components/AdminMembershipAgreementPanel";

const STATUS_LABELS = {
  DRAFT: "Draft",
  PENDING_VERIFICATION: "Pending",
  VERIFIED: "Verified",
  REJECTED: "Rejected",
  SUSPENDED: "Suspended",
};

const DOC_LABELS = {
  GST_CERTIFICATE: "GST Certificate",
  PAN_DOCUMENT: "PAN Document",
  INCORPORATION_CERTIFICATE: "Incorporation Certificate",
  ADDRESS_PROOF: "Business Address Proof",
  OTHER: "Other Supporting Document",
};

const REVIEWER_ROLES = ["SUPER_ADMIN", "OPERATIONS_ADMIN"];

const DIALOG_TITLES = {
  approve: "Approve company",
  reject: "Reject company",
  suspend: "Suspend company",
  reactivate: "Reactivate company",
};

function SubscribeDialog({ onClose, onSubmit }) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [chequeNumber, setChequeNumber] = useState("");
  const [bankName, setBankName] = useState("");
  useEffect(() => {
    client.get("/admin/subscription-plans").then((r) => { setPlans(r.data); setPlanId(r.data[0]?.id || ""); }).catch(() => {});
  }, []);
  const submit = async (event) => {
    event.preventDefault();
    if (!planId || !chequeNumber.trim() || !bankName.trim()) return;
    setBusy(true); setError("");
    try { await onSubmit({ plan_id: planId, security_cheque_number: chequeNumber.trim(), security_cheque_bank_name: bankName.trim() }); onClose(); }
    catch (err) { setError(extractError(err, "Unable to assign plan.")); }
    finally { setBusy(false); }
  };
  const selectedPlan = plans.find((plan) => plan.id === planId);
  return (
    <div className="dialog-backdrop" role="dialog" data-testid="dialog-subscribe">
      <form className="dialog" onSubmit={submit}>
        <h3>Assign subscription plan</h3>
        <label>Plan *
          <select required value={planId} onChange={(e) => setPlanId(e.target.value)} data-testid="subscribe-plan-select">
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · ₹{p.subscription_fee.toLocaleString("en-IN")} → ₹{p.initial_credit_limit.toLocaleString("en-IN")} credit</option>)}
          </select>
        </label>
        <div className="grid-2">
          <label>Cheque Number *<input required value={chequeNumber} onChange={(e) => setChequeNumber(e.target.value)} maxLength={80} data-testid="subscribe-cheque-number-input" /></label>
          <label>Bank Name *<input required value={bankName} onChange={(e) => setBankName(e.target.value)} maxLength={200} data-testid="subscribe-bank-name-input" /></label>
        </div>
        <div className="agreement-input-summary">
          <div><span>Security Cheque Amount</span><strong data-testid="subscribe-security-cheque-amount">₹{Number(selectedPlan?.initial_credit_limit || 0).toLocaleString("en-IN")}</strong></div>
          <div><span>Cheque Date</span><strong data-testid="subscribe-cheque-date">BLANK</strong></div>
        </div>
        <p className="muted small">Assigning a plan will issue the initial trade credit limit to the company.</p>
        {error && <div className="form-error" data-testid="subscribe-error">{error}</div>}
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onClose} data-testid="subscribe-cancel">Cancel</button>
          <button type="submit" className="primary-button" disabled={busy || !planId || !chequeNumber.trim() || !bankName.trim()} data-testid="subscribe-confirm">{busy ? "Assigning…" : "Assign plan"}</button>
        </div>
      </form>
    </div>
  );
}

function AdjustCreditDialog({ onClose, onSubmit }) {
  const [direction, setDirection] = useState("credit");
  const [amount, setAmount] = useState(1000);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event) => {
    event.preventDefault();
    if (reason.trim().length < 5) { setError("Reason must be at least 5 characters."); return; }
    setBusy(true); setError("");
    try { await onSubmit({ direction, amount: Number(amount), reason }); onClose(); }
    catch (err) { setError(extractError(err, "Adjustment failed.")); }
    finally { setBusy(false); }
  };
  return (
    <div className="dialog-backdrop" role="dialog" data-testid="dialog-adjust">
      <form className="dialog" onSubmit={submit}>
        <h3>Manual credit adjustment</h3>
        <div className="grid-2">
          <label>Direction *
            <select value={direction} onChange={(e) => setDirection(e.target.value)} data-testid="adjust-direction">
              <option value="credit">Credit (increase limit)</option>
              <option value="debit">Debit (reduce limit)</option>
            </select>
          </label>
          <label>Amount (₹) *<input required type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="adjust-amount" /></label>
        </div>
        <label>Reason (required, recorded in audit) *
          <textarea required minLength={5} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjust-reason" placeholder="Explain why this adjustment is needed" />
        </label>
        {error && <div className="form-error" data-testid="adjust-credit-error">{error}</div>}
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onClose} data-testid="adjust-cancel">Cancel</button>
          <button type="submit" className="primary-button" disabled={busy} data-testid="adjust-confirm">{busy ? "Working…" : "Apply adjustment"}</button>
        </div>
      </form>
    </div>
  );
}

function ActionDialog({ title, mode, reasonCodes, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [reasonCode, setReasonCode] = useState(reasonCodes?.[0]?.value || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const requiresReason = mode === "reject" || mode === "suspend";
  const requiresReasonCode = mode === "reject";

  const submit = async (event) => {
    event.preventDefault();
    if (requiresReason && reason.trim().length < 5) {
      setError("Reason must be at least 5 characters.");
      return;
    }
    setBusy(true); setError("");
    try {
      const payload = { admin_notes: notes || undefined };
      if (requiresReason) payload.reason = reason;
      if (requiresReasonCode) payload.reason_code = reasonCode;
      if (mode === "reactivate" && reason) payload.reason = reason;
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(extractError(err, "Unable to complete action."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-backdrop" role="dialog" data-testid={`dialog-${mode}`}>
      <form className="dialog" onSubmit={submit}>
        <h3>{title}</h3>
        {requiresReasonCode && (
          <label>Reason category *
            <select required value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} data-testid={`dialog-${mode}-reason-code`}>
              {reasonCodes.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
        )}
        {requiresReason && (
          <label>Reason (shared with the company) *
            <textarea required minLength={5} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`dialog-${mode}-reason`} placeholder="Describe what needs to change" />
          </label>
        )}
        {mode === "reactivate" && (
          <label>Reason (optional)
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`dialog-${mode}-reason`} placeholder="Why is this company being reactivated?" />
          </label>
        )}
        <label>Internal admin notes (private)
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`dialog-${mode}-notes`} placeholder="Only visible to Barter India administrators" />
        </label>
        {error && <div className="form-error" data-testid={`dialog-${mode}-error`}>{error}</div>}
        <div className="dialog-actions">
          <button type="button" className="ghost-button" data-testid={`dialog-${mode}-cancel`} onClick={onClose}>Cancel</button>
          <button type="submit" className="primary-button" data-testid={`dialog-${mode}-confirm`} disabled={busy}>
            {busy ? "Working…" : "Confirm"}
          </button>
        </div>
      </form>
    </div>
  );
}

const FILTER_OPTIONS_ALL = [
  { value: "", label: "All companies" },
  { value: "PENDING_VERIFICATION", label: "Pending" },
  { value: "VERIFIED", label: "Verified" },
  { value: "REJECTED", label: "Rejected" },
  { value: "SUSPENDED", label: "Suspended" },
];

export default function AdminCompaniesPage({ user, fixedStatus, hideTabs, initialCompanyId, onConsumeInitial }) {
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState(fixedStatus || "");
  const [search, setSearch] = useState("");
  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [message, setMessage] = useState("");

  const canReview = REVIEWER_ROLES.includes(user.role);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("MANUAL_CONFIRMATION");
  const [paymentChequeNumber, setPaymentChequeNumber] = useState("");
  const [paymentBankName, setPaymentBankName] = useState("");
  const [agreementRefreshKey, setAgreementRefreshKey] = useState(0);
  const [passwordResetBusy, setPasswordResetBusy] = useState(false);

  const doSendPasswordReset = async (userId, email) => {
    if (!userId) return;
    if (!window.confirm(`Send a secure password reset link to ${email}? The customer will receive an email that expires in 60 minutes.`)) return;
    setPasswordResetBusy(true);
    setError("");
    try {
      await client.post(`/admin/users/${userId}/password-reset`);
      setMessage(`Password reset email queued for ${email}.`);
    } catch (err) {
      setError(extractError(err, "Unable to send password reset email."));
    } finally {
      setPasswordResetBusy(false);
    }
  };

  useEffect(() => {
    client.get("/public/subscription-plans")
      .then((r) => setAllPlans(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAllPlans([]));
  }, []);

  const loadSubscription = async (id) => {
    try {
      const res = await client.get(`/admin/companies/${id}/subscription`);
      setSubscriptionData(res.data);
    } catch { setSubscriptionData(null); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, c, rc] = await Promise.all([
          client.get("/reference/states"),
          client.get("/reference/categories"),
          client.get("/admin/companies/reason-codes").catch(() => ({ data: [] })),
        ]);
        setStates(s.data); setCategories(c.data); setReasonCodes(rc.data);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!stateId) { setCities([]); setCityId(""); return; }
    client.get(`/reference/cities?state_id=${stateId}`).then((r) => setCities(r.data)).catch(() => setCities([]));
  }, [stateId]);

  const load = async (targetPage = 1) => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      const activeFilter = fixedStatus || filter;
      if (activeFilter) params.set("status", activeFilter);
      if (search) params.set("search", search);
      if (stateId) params.set("state_id", stateId);
      if (cityId) params.set("city_id", cityId);
      if (categoryId) params.set("category_id", categoryId);
      params.set("page", targetPage);
      params.set("page_size", 25);
      const res = await client.get(`/admin/companies?${params.toString()}`);
      setList(res.data.items || []);
      setCounts(res.data.counts || {});
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      setError(extractError(err, "Unable to load companies."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [filter, fixedStatus]);

  useEffect(() => {
    if (initialCompanyId) {
      openDetail(initialCompanyId);
      onConsumeInitial?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCompanyId]);

  const openDetail = async (companyId) => {
    setSelectedId(companyId); setDetail(null); setMessage(""); setSubscriptionData(null);
    setPaymentChequeNumber(""); setPaymentBankName("");
    setPaymentMethod("MANUAL_CONFIRMATION");
    try {
      const res = await client.get(`/admin/companies/${companyId}`);
      setDetail(res.data);
      loadSubscription(companyId);
    } catch (err) {
      setError(extractError(err, "Unable to load company detail."));
    }
  };

  const closeDetail = () => { setSelectedId(null); setDetail(null); };

  const doAction = (mode) => async (payload) => {
    const res = await client.post(`/admin/companies/${detail.id}/${mode}`, payload);
    setDetail(res.data);
    setMessage(`Company ${mode === "reactivate" ? "reactivated" : mode + "d"} successfully.`);
    const full = await client.get(`/admin/companies/${detail.id}`);
    setDetail(full.data);
    loadSubscription(detail.id);
    load(page);
  };

  const doSubscribe = async (payload) => {
    await client.post(`/admin/companies/${detail.id}/subscribe`, payload);
    setMessage("Subscription plan assigned and initial credit issued.");
    loadSubscription(detail.id);
    setAgreementRefreshKey((value) => value + 1);
  };

  const doAdjust = async (payload) => {
    await client.post(`/admin/companies/${detail.id}/credit-adjustment`, payload);
    setMessage("Credit adjustment recorded.");
    loadSubscription(detail.id);
  };

  const doConfirmPayment = async () => {
    if (!detail) return;
    setConfirmingPayment(true);
    setError("");
    try {
      const body = {
        security_cheque_number: paymentChequeNumber.trim(),
        security_cheque_bank_name: paymentBankName.trim(),
        payment_method: paymentMethod,
        ...(paymentReference.trim() ? { payment_reference: paymentReference.trim() } : {}),
      };
      const res = await client.post(`/admin/companies/${detail.id}/confirm-payment`, body);
      setDetail(res.data);
      setMessage("Payment confirmed. Subscription activated and Trade Credits allocated.");
      setPaymentReference("");
      setPaymentMethod("MANUAL_CONFIRMATION");
      setPaymentChequeNumber(""); setPaymentBankName("");
      loadSubscription(detail.id);
      setAgreementRefreshKey((value) => value + 1);
      load(page);
    } catch (err) {
      setError(extractError(err, "Unable to confirm payment."));
    } finally {
      setConfirmingPayment(false);
    }
  };

  const viewDocument = async (doc) => {
    try {
      const res = await client.get(`/admin/companies/${detail.id}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(extractError(err, "Unable to open document."));
    }
  };

  const filters = useMemo(() => FILTER_OPTIONS_ALL, []);

  if (selectedId && detail) {
    return (
      <div className="admin-company-detail" data-testid="admin-company-detail">
        <button type="button" className="link-button back" data-testid="back-to-list" onClick={closeDetail}>
          <ChevronLeft size={16} /> Back to companies
        </button>
        <header className="detail-header">
          <div>
            <p className="eyebrow">COMPANY REVIEW</p>
            <h2 data-testid="detail-legal-name">{detail.legal_name}</h2>
            <p className="muted">{detail.brand_name ? `${detail.brand_name} · ` : ""}{detail.state_name}{detail.city_name ? `, ${detail.city_name}` : ""}</p>
          </div>
          <span className={`status-chip status-${detail.status.toLowerCase()}`} data-testid="detail-status">
            {STATUS_LABELS[detail.status]}
          </span>
        </header>

        {message && <div className="form-success" data-testid="detail-message">{message}</div>}
        {error && <div className="form-error" data-testid="admin-company-error">{error}</div>}

        {canReview && (
          <div className="detail-actions">
            <button type="button" className="primary-button" data-testid="approve-company-button" disabled={detail.status !== "PENDING_VERIFICATION"} onClick={() => setDialog({ mode: "approve" })}>
              <CheckCircle2 size={16} /> Approve
            </button>
            <button type="button" className="danger-button-solid" data-testid="reject-company-button" disabled={detail.status !== "PENDING_VERIFICATION"} onClick={() => setDialog({ mode: "reject" })}>
              <XCircle size={16} /> Reject
            </button>
            <button type="button" className="ghost-button" data-testid="suspend-company-button" disabled={detail.status !== "VERIFIED"} onClick={() => setDialog({ mode: "suspend" })}>
              <Ban size={16} /> Suspend
            </button>
            <button type="button" className="ghost-button" data-testid="reactivate-company-button" disabled={detail.status !== "SUSPENDED"} onClick={() => setDialog({ mode: "reactivate" })}>
              <RotateCcw size={16} /> Reactivate
            </button>
            <button type="button" className="ghost-button" data-testid="subscribe-company-button" disabled={detail.status !== "VERIFIED"} onClick={() => setDialog({ mode: "subscribe" })}>
              <CreditCard size={16} /> {subscriptionData?.subscription ? "Change plan" : "Assign plan"}
            </button>
            <button type="button" className="ghost-button" data-testid="adjust-credit-button" disabled={detail.status !== "VERIFIED" || !subscriptionData?.credit_account} onClick={() => setDialog({ mode: "adjust" })}>
              <Coins size={16} /> Adjust credit
            </button>
          </div>
        )}

        {subscriptionData?.credit_account && (
          <section className="credit-panel" data-testid="admin-credit-panel">
            <div className="section-heading"><div><p className="eyebrow">CREDIT ACCOUNT</p><h3>Trade credit balance</h3></div></div>
            <div className="credit-stats-row">
              <div><p className="eyebrow">PLAN</p><strong data-testid="admin-plan-name">{subscriptionData.subscription?.plan_name || "—"}</strong></div>
              <div><p className="eyebrow">LIMIT</p><strong data-testid="admin-credit-limit">₹{subscriptionData.credit_account.current_credit_limit.toLocaleString("en-IN")}</strong></div>
              <div><p className="eyebrow">USED</p><strong>₹{subscriptionData.credit_account.used_credit.toLocaleString("en-IN")}</strong></div>
              <div><p className="eyebrow">AVAILABLE</p><strong>₹{subscriptionData.credit_account.available_credit.toLocaleString("en-IN")}</strong></div>
              <div><p className="eyebrow">STATUS</p><strong>{subscriptionData.credit_account.status}</strong></div>
            </div>
          </section>
        )}

        {detail.selected_plan_id && detail.status === "VERIFIED" && detail.payment_status &&
         ["PENDING", "RECEIVED"].includes(detail.payment_status) && (() => {
          const plan = allPlans.find((p) => p.id === detail.selected_plan_id);
          const quote = detail.subscription_invoice_quote;
          const subscriptionFee = quote?.subtotal ?? plan?.subscription_fee ?? 0;
          const gstAmount = quote?.gst_amount ?? 0;
          const amount = quote?.total ?? 0;
          const securityAmount = plan?.initial_credit_limit || 0;
          const message = buildPaymentRequestMessage({
            contactName: detail.contact_person_name,
            companyName: detail.legal_name,
            planName: plan?.name || "—",
            amount,
          });
          const waUrl = quote ? buildWhatsappUrl(detail.contact_phone, message) : null;
          const waNormalized = normalizePhoneForWhatsapp(detail.contact_phone);
          const isReceived = detail.payment_status === "RECEIVED";
          return (
            <section className="credit-panel" data-testid="admin-payment-panel">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">SUBSCRIPTION PAYMENT</p>
                  <h3>APPROVED &rarr; {isReceived ? "PAYMENT CONFIRMED · ACTIVE" : "PAYMENT PENDING"}</h3>
                </div>
                <span className={`status-chip ${isReceived ? "status-verified" : "status-pending_verification"}`} data-testid="detail-payment-status-chip">
                  {isReceived ? "Payment received" : "Payment pending"}
                </span>
              </div>
              <div className="credit-stats-row">
                <div><p className="eyebrow">SELECTED PLAN</p><strong data-testid="admin-selected-plan-name">{plan?.name || detail.selected_plan_id}</strong></div>
                <div><p className="eyebrow">SUBSCRIPTION FEE</p><strong data-testid="admin-selected-plan-amount">&#8377;{Number(subscriptionFee).toLocaleString("en-IN")}</strong></div>
                <div><p className="eyebrow">GST ({Number(quote?.gst_rate_percent || 0)}%)</p><strong data-testid="admin-subscription-gst">&#8377;{Number(gstAmount).toLocaleString("en-IN")}</strong></div>
                <div><p className="eyebrow">TOTAL PAYABLE</p><strong data-testid="admin-subscription-total">&#8377;{Number(amount).toLocaleString("en-IN")}</strong></div>
                {quote?.is_interstate === false && <div><p className="eyebrow">GST BREAKUP</p><strong data-testid="admin-subscription-gst-breakup">CGST ₹{Number(quote.cgst_amount).toLocaleString("en-IN")} + SGST ₹{Number(quote.sgst_amount).toLocaleString("en-IN")}</strong></div>}
                {quote?.is_interstate === true && <div><p className="eyebrow">GST BREAKUP</p><strong data-testid="admin-subscription-gst-breakup">IGST ₹{Number(quote.igst_amount).toLocaleString("en-IN")}</strong></div>}
                <div><p className="eyebrow">CONTACT NUMBER</p><strong data-testid="admin-contact-number">{detail.contact_phone || "—"}</strong></div>
                <div><p className="eyebrow">PAYMENT STATUS</p><strong>{detail.payment_status}</strong></div>
                {isReceived && detail.payment_confirmed_at && (
                  <div><p className="eyebrow">CONFIRMED AT</p><strong>{new Date(detail.payment_confirmed_at).toLocaleString()}</strong></div>
                )}
                {isReceived && detail.payment_reference && (
                  <div><p className="eyebrow">REFERENCE</p><strong>{detail.payment_reference}</strong></div>
                )}
              </div>
              {!isReceived && (
                <>
                {canReview && (
                  <div className="agreement-cheque-form" data-testid="confirm-payment-cheque-form">
                    <label>Cheque Number *<input required value={paymentChequeNumber} onChange={(e) => setPaymentChequeNumber(e.target.value)} maxLength={80} data-testid="payment-cheque-number-input" /></label>
                    <label>Bank Name *<input required value={paymentBankName} onChange={(e) => setPaymentBankName(e.target.value)} maxLength={200} data-testid="payment-bank-name-input" /></label>
                    <div><span>Security Cheque Amount</span><strong data-testid="payment-security-cheque-amount">₹{Number(securityAmount).toLocaleString("en-IN")}</strong></div>
                    <div><span>Cheque Date</span><strong data-testid="payment-cheque-date">BLANK</strong></div>
                  </div>
                )}
                {detail.subscription_invoice_quote_error && (
                  <p className="form-error small" data-testid="subscription-quote-error">{detail.subscription_invoice_quote_error}</p>
                )}
                <div className="detail-actions">
                  {waUrl ? (
                    <a
                      className="ghost-button"
                      href={waUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid="whatsapp-request-link"
                    >
                      <MessageCircle size={16} /> Send Payment Request on WhatsApp
                    </a>
                  ) : (
                    <button type="button" className="ghost-button" disabled data-testid="whatsapp-request-disabled"
                            title="Customer contact number is not a valid Indian/international mobile number.">
                      <MessageCircle size={16} /> WhatsApp unavailable
                    </button>
                  )}
                  {canReview && (
                    <>
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        data-testid="payment-method-select"
                        aria-label="Payment method"
                        style={{ maxWidth: 220 }}
                      >
                        <option value="MANUAL_CONFIRMATION">Manual confirmation</option>
                        <option value="NEFT">NEFT</option>
                        <option value="RTGS">RTGS</option>
                        <option value="IMPS">IMPS</option>
                        <option value="UPI">UPI</option>
                        <option value="CHEQUE">Cheque</option>
                        <option value="CASH">Cash</option>
                        <option value="OTHER">Other</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Payment reference (optional)"
                        value={paymentReference}
                        onChange={(e) => setPaymentReference(e.target.value)}
                        data-testid="payment-reference-input"
                        maxLength={120}
                        style={{ maxWidth: 260 }}
                      />
                      <button
                        type="button"
                        className="primary-button"
                        onClick={doConfirmPayment}
                        disabled={confirmingPayment || !quote || !paymentChequeNumber.trim() || !paymentBankName.trim()}
                        data-testid="confirm-payment-button"
                      >
                        <CheckSquare size={16} /> {confirmingPayment ? "Confirming…" : "Confirm Payment"}
                      </button>
                    </>
                  )}
                </div>
                </>
              )}
              {!isReceived && !waNormalized && (
                <p className="form-error small" data-testid="whatsapp-invalid-phone">
                  Contact number is missing or invalid. Ask the customer to update it before sending the payment request.
                </p>
              )}
            </section>
          );
        })()}

        <AdminMembershipAgreementPanel
          companyId={detail.id}
          refreshKey={agreementRefreshKey}
          canRetry={canReview}
          onError={setError}
        />

        {detail.rejection_reason && (
          <div className="review-callout rejected" data-testid="detail-rejection-reason"><p className="eyebrow">REJECTION REASON (SHARED WITH COMPANY)</p><p>{detail.rejection_reason}</p></div>
        )}
        {detail.suspension_reason && (
          <div className="review-callout suspended"><p className="eyebrow">SUSPENSION REASON</p><p>{detail.suspension_reason}</p></div>
        )}
        {detail.admin_review_notes && (
          <div className="review-callout internal" data-testid="detail-admin-notes"><p className="eyebrow">INTERNAL ADMIN NOTES (PRIVATE)</p><p>{detail.admin_review_notes}</p></div>
        )}

        <section className="detail-grid" data-testid="detail-grid">
          <article><p className="eyebrow">LEGAL NAME</p><p>{detail.legal_name}</p></article>
          <article><p className="eyebrow">BRAND</p><p>{detail.brand_name}</p></article>
          <article><p className="eyebrow">BUSINESS TYPE</p><p>{detail.business_type}</p></article>
          <article><p className="eyebrow">GSTIN</p><p>{detail.gstin || "—"}</p></article>
          <article><p className="eyebrow">PAN</p><p>{detail.pan}</p></article>
          <article><p className="eyebrow">WEBSITE</p><p>{detail.website || "—"}</p></article>
          <article><p className="eyebrow">CATEGORY</p><p>{detail.primary_category_name}{detail.primary_subcategory_name ? ` · ${detail.primary_subcategory_name}` : ""}</p></article>
          <article><p className="eyebrow">CONTACT</p><p>{detail.contact_person_name}<br /><small>{detail.contact_designation}</small><br /><small>{detail.contact_email} · {detail.contact_phone}</small></p></article>
          <article><p className="eyebrow">LOCATION</p><p>{detail.area}, {detail.city_name}, {detail.state_name} — {detail.pin_code}</p></article>
          <article className="wide"><p className="eyebrow">REGISTERED OFFICE ADDRESS</p><p data-testid="detail-registered-office-address">{detail.registered_office_address || "—"}</p></article>
          <article><p className="eyebrow">SUBMITTED</p><p>{detail.submitted_at ? new Date(detail.submitted_at).toLocaleString() : "Not submitted"}</p></article>
          <article><p className="eyebrow">REVIEWED</p><p>{detail.reviewed_at ? new Date(detail.reviewed_at).toLocaleString() : "—"}{detail.reviewed_by_email ? <><br /><small>by {detail.reviewed_by_email}</small></> : null}</p></article>
          <article><p className="eyebrow">OWNER</p><p>{detail.owner_name || "—"}<br /><small>{detail.owner_email}</small>{detail.owner_user_id && canReview && (<><br /><button type="button" className="link-button" data-testid="admin-send-password-reset" disabled={passwordResetBusy} onClick={() => doSendPasswordReset(detail.owner_user_id, detail.owner_email)}>{passwordResetBusy ? "Sending…" : "Send Password Reset Link"}</button></>)}</p></article>
          <article className="wide"><p className="eyebrow">DESCRIPTION</p><p>{detail.business_description}</p></article>
        </section>

        <section className="documents-section">
          <div className="section-heading"><div><p className="eyebrow">SUBMITTED DOCUMENTS</p><h3>Verification records</h3></div></div>
          <ul className="document-list">
            {(detail.documents || []).length === 0 && <li className="muted small">No documents uploaded yet.</li>}
            {(detail.documents || []).map((doc) => (
              <li key={doc.id} data-testid={`admin-document-row-${doc.id}`}>
                <FileText size={16} />
                <div>
                  <strong>{DOC_LABELS[doc.document_type] || doc.document_type}</strong>
                  <small>{doc.file_name} · {(doc.file_size / 1024).toFixed(1)} KB · {new Date(doc.created_at).toLocaleDateString()}</small>
                </div>
                <button type="button" className="link-button" data-testid={`admin-view-document-${doc.id}`} onClick={() => viewDocument(doc)}>View Document</button>
              </li>
            ))}
          </ul>
        </section>

        <section className="documents-section">
          <div className="section-heading"><div><p className="eyebrow">AUDIT LOG</p><h3>Verification history</h3></div></div>
          <ul className="audit-list" data-testid="audit-log-list">
            {(detail.audit_log || []).length === 0 && <li className="muted small">No audit entries yet.</li>}
            {(detail.audit_log || []).map((entry) => (
              <li key={entry.id}>
                <div className="audit-status">
                  <span className={`status-chip status-${entry.new_status.toLowerCase()}`}>{STATUS_LABELS[entry.new_status] || entry.new_status}</span>
                  <small>from {entry.previous_status ? STATUS_LABELS[entry.previous_status] || entry.previous_status : "—"}</small>
                </div>
                <div className="audit-meta">
                  <small>{new Date(entry.created_at).toLocaleString()}</small>
                  <small>{entry.actor_email || "system"} ({entry.actor_role || "—"})</small>
                </div>
                {entry.reason && <p className="audit-reason">Reason: {entry.reason}</p>}
                {entry.admin_notes && <p className="audit-notes">Notes: {entry.admin_notes}</p>}
              </li>
            ))}
          </ul>
        </section>

        {dialog && dialog.mode === "subscribe" && (
          <SubscribeDialog onClose={() => setDialog(null)} onSubmit={doSubscribe} />
        )}
        {dialog && dialog.mode === "adjust" && (
          <AdjustCreditDialog onClose={() => setDialog(null)} onSubmit={doAdjust} />
        )}
        {dialog && !["subscribe", "adjust"].includes(dialog.mode) && (
          <ActionDialog
            title={DIALOG_TITLES[dialog.mode]}
            mode={dialog.mode}
            reasonCodes={reasonCodes}
            onClose={() => setDialog(null)}
            onSubmit={doAction(dialog.mode)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="admin-companies-page" data-testid="admin-companies-page">
      <div className="admin-toolbar">
        {!hideTabs && (
          <div className="filter-tabs" data-testid="admin-status-tabs">
            {filters.map((opt) => (
              <button
                key={opt.value || "all"}
                type="button"
                data-testid={`filter-${opt.value || "all"}`}
                className={filter === opt.value ? "active" : ""}
                onClick={() => { setFilter(opt.value); }}
              >
                {opt.label}
                {opt.value && counts[opt.value] > 0 && <span className="count">{counts[opt.value]}</span>}
              </button>
            ))}
          </div>
        )}
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); load(1); }}>
          <input placeholder="Search legal name, brand, GSTIN, PAN, email" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-search-input" />
          <button type="submit" className="ghost-button" data-testid="admin-search-button">Search</button>
        </form>
      </div>
      <div className="advanced-filters" data-testid="advanced-filters">
        <select data-testid="filter-state" value={stateId} onChange={(e) => setStateId(e.target.value)}>
          <option value="">All states</option>
          {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select data-testid="filter-city" value={cityId} onChange={(e) => setCityId(e.target.value)} disabled={!stateId}>
          <option value="">{stateId ? "All cities" : "Choose state first"}</option>
          {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="filter-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button type="button" className="ghost-button" data-testid="apply-filters-button" onClick={() => load(1)}>Apply filters</button>
        <button type="button" className="link-button" data-testid="clear-filters-button" onClick={() => { setStateId(""); setCityId(""); setCategoryId(""); setSearch(""); setTimeout(() => load(1), 0); }}>Clear</button>
      </div>

      {error && <div className="form-error" data-testid="admin-companies-error">{error}</div>}
      {loading ? <div className="loading-block">Loading companies…</div> : (
        <>
          <table className="admin-table" data-testid="admin-companies-table">
            <thead>
              <tr>
                <th>Company</th><th>GSTIN / PAN</th><th>Location</th><th>Category</th><th>Contact</th><th>Registered</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={8} className="empty-row">No companies match this view.</td></tr>
              )}
              {list.map((c) => (
                <tr key={c.id} data-testid={`admin-company-row-${c.id}`}>
                  <td>
                    <strong>{c.legal_name}</strong>
                    {c.brand_name && <><br /><small className="muted">{c.brand_name}</small></>}
                  </td>
                  <td><code>{c.gstin || "—"}</code><br /><small className="muted">{c.pan}</small></td>
                  <td>{c.city_name}, {c.state_name}</td>
                  <td>{c.primary_category_name || "—"}</td>
                  <td>{c.contact_person_name}<br /><small className="muted">{c.contact_email}</small></td>
                  <td>{c.submitted_at ? new Date(c.submitted_at).toLocaleDateString() : new Date(c.created_at).toLocaleDateString()}</td>
                  <td><span className={`status-chip status-${c.status.toLowerCase()}`}>{STATUS_LABELS[c.status]}</span></td>
                  <td><button type="button" className="link-button" data-testid={`review-company-${c.id}`} onClick={() => openDetail(c.id)}>Review →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination" data-testid="companies-pagination">
            <button type="button" className="ghost-button" data-testid="prev-page" onClick={() => load(page - 1)} disabled={page <= 1}>
              <ChevronLeft size={14} /> Previous
            </button>
            <span>Page {page} of {totalPages} · {total} companies</span>
            <button type="button" className="ghost-button" data-testid="next-page" onClick={() => load(page + 1)} disabled={page >= totalPages}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
